"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatItemData = exports.formatFieldData = void 0;
const tslib_1 = require("tslib");
const bignumber_js_1 = tslib_1.__importDefault(require("bignumber.js"));
const { formatMoneyInOrder } = require('@futuweb/tool-formatter');
const conf_1 = require("../conf");
/**
 * 格式化字段数据
 * @param {string} fieldKey 字段key
 * @param {any} rawValue 原始值
 * @returns {object} 格式化后的数据 { txt, unitTxt, cls, tickerType?, color? }
 */
const formatFieldData = (fieldKey, rawValue, $tran, lang) => {
    // 获取字段配置，如果不存在则使用默认配置
    const fieldConfig = conf_1.SHOW_FIELD_DATA_MAP[fieldKey] || conf_1.DEFAULT_FIELD_CONFIG;
    const { unit = 1, fixedUnit, hasRate, hasNoMinusRate, hasColor, cls: defaultCls = '', unitTxt: defaultUnitTxt = '', formatMoney } = fieldConfig;
    // 空值处理
    if (rawValue === undefined || rawValue === null || rawValue === '') {
        return {
            txt: fieldConfig.txt || '---',
            unitTxt: defaultUnitTxt,
            cls: defaultCls
        };
    }
    // 特殊处理：direction/tickerType 字段（枚举值转换）
    // TickerType: 0=UNKNOWN, 1=BUY, 2=SELL, 3=NEUTRAL
    if (fieldKey === 'direction' || fieldKey === 'tickerType') {
        const tickerTypeValue = Number(rawValue);
        let tickerType;
        let txt;
        let color;
        switch (tickerTypeValue) {
            case conf_1.TICKER_TYPE.BUY:
                tickerType = 'buy';
                txt = $tran('buy');
                color = 'rgba(0, 168, 112, 1)'; // 主动买入：绿色
                break;
            case conf_1.TICKER_TYPE.SELL:
                tickerType = 'sell';
                txt = $tran('sell');
                color = 'rgba(240, 58, 85, 1)'; // 主动卖出：红色
                break;
            case conf_1.TICKER_TYPE.NEUTRAL:
                tickerType = 'neutral';
                txt = $tran('neutral') || '中性';
                color = ''; // 中性不设置颜色
                break;
            case conf_1.TICKER_TYPE.UNKNOWN:
            default:
                tickerType = 'unknown';
                txt = $tran('unknown') || '未知';
                color = ''; // 未知不设置颜色
                break;
        }
        return {
            txt,
            unitTxt: defaultUnitTxt,
            cls: defaultCls,
            tickerType,
            color
        };
    }
    // 使用 BigNumber 转换为数值，避免精度问题
    let num;
    try {
        num = new bignumber_js_1.default(rawValue);
        if (!num.isFinite() || num.isNaN()) {
            return conf_1.DEFAULT_FIELD_CONFIG;
        }
    }
    catch (error) {
        return conf_1.DEFAULT_FIELD_CONFIG;
    }
    // 特殊处理：iv/optionIv 字段（单位是 0.001%，需要特殊转换）
    if (fieldKey === 'iv' || fieldKey === 'optionIv') {
        // optionIv 单位是 0.001%，需要除以1000得到百分比
        // 例如：180730 (0.001%单位) -> 180730 * 0.001% = 180.73%
        // 计算：180730 / 1000 = 180.73，然后显示为 "180.73%"
        const percent = num.dividedBy(conf_1.OPTION_IV_UNIT_DIVISOR);
        return Object.assign(Object.assign({}, conf_1.DEFAULT_FIELD_CONFIG), { txt: `${percent.toFixed(conf_1.DEFAULT_DECIMAL_PLACES)}%` });
    }
    // 根据配置的 unit 进行单位转换
    if (unit > 1) {
        num = num.dividedBy(unit);
    }
    // 初始化返回数据，使用配置中的默认值
    const retData = {
        txt: '',
        cls: defaultCls
    };
    // 根据配置处理颜色类（hasColor）
    if (hasColor) {
        // 对于成交额、价格、成交量等字段，通常都是正数，显示为 'up' 类
        // 如果有负数，则显示为 'down' 类
        retData.cls = num.isPositive() || num.isZero() ? 'up' : 'down';
    }
    // 根据配置处理金额（formatMoney）- 优先处理，因为这是更高级的格式化方式
    // 对于成交额和成交量，需要使用 formatMoneyInOrder 进行格式化（如：1.2万、1.5K等）
    if (formatMoney) {
        const langTxt = lang === conf_1.LANG_MODE_MAP.HK ? 'hk' : 'zh';
        if (lang === conf_1.LANG_MODE_MAP.EN) {
            retData.txt = formatMoneyInOrder(num.toNumber());
        }
        else {
            retData.txt = formatMoneyInOrder(num.toNumber(), langTxt);
        }
        return retData;
    }
    // 根据配置处理百分比（hasRate）
    if (hasRate) {
        const percent = num.multipliedBy(conf_1.PERCENTAGE_MULTIPLIER);
        const sign = percent.isPositive() ? '+' : '';
        retData.txt = `${sign}${percent.toFixed(conf_1.DEFAULT_DECIMAL_PLACES)}%`;
        return retData;
    }
    // 根据配置处理不带符号的百分比（hasNoMinusRate）
    if (hasNoMinusRate) {
        const percent = num.multipliedBy(conf_1.PERCENTAGE_MULTIPLIER);
        retData.txt = `${percent.toFixed(conf_1.DEFAULT_DECIMAL_PLACES)}%`;
        return retData;
    }
    // 根据配置处理固定小数位（fixedUnit）
    if (fixedUnit !== undefined) {
        retData.txt = num.toFixed(fixedUnit);
        return retData;
    }
    // 默认格式化为2位小数
    retData.txt = num.toFixed(conf_1.DEFAULT_DECIMAL_PLACES);
    return retData;
};
exports.formatFieldData = formatFieldData;
/**
 * 格式化整条数据
 * @param {object} item 原始数据项
 * @param {string[]} fieldList 需要格式化的字段列表（从 showFieldArr 中提取）
 * @returns {object} 格式化后的数据项
 */
const formatItemData = (item, fieldList, $tran, lang) => {
    const formatted = {
        // 只保留基础字段，不复制所有原始字段
        optionId: item.optionId,
        optionName: item.optionName,
        time: item.time,
        productCode: item.productCode,
        optionType: item.optionType,
        expiryDays: item.expiryDays,
    };
    // 只格式化 showFieldArr 中指定的字段
    fieldList.forEach((fieldKey) => {
        const rawValue = item[fieldKey];
        // 如果字段值存在（不为 undefined、null 或空字符串），则格式化
        // 如果字段值不存在，formatFieldData 会返回默认值（'---'）
        const formattedValue = (0, exports.formatFieldData)(fieldKey, rawValue, $tran, lang);
        formatted[fieldKey] = formattedValue;
    });
    return formatted;
};
exports.formatItemData = formatItemData;
//# sourceMappingURL=data:application/json;base64,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